import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Stack, Title, Text, Loader, Center, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAssetByToken } from '@/hooks/api/useAssetTokens';
import { useAuth } from '@/hooks/useAuth';

/**
 * Asset Token Page - Handles QR code scans via /a/:token route
 *
 * This page:
 * 1. Accepts a token from the URL (/a/XXXX-XXXX)
 * 2. Looks up the asset by token
 * 3. Records the scan
 * 4. If authenticated → redirects to /assets?view=<asset-id>
 * 5. If not authenticated → redirects to /login with return URL
 */
export function AssetTokenPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: asset, isLoading, error } = useAssetByToken(token);

  useEffect(() => {
    if (!token) {
      navigate('/assets');
      return;
    }

    // If not authenticated, redirect to login with return URL
    if (!user) {
      const returnUrl = `/a/${token}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // If asset loaded successfully, redirect to assets page with view param
    if (asset && !isLoading) {
      navigate(`/assets?view=${asset.id}`);
    }
  }, [token, user, asset, isLoading, navigate]);

  // Show loading state while fetching or redirecting
  if (isLoading || asset) {
    return (
      <Container size="sm" py="xl">
        <Center style={{ minHeight: '50vh' }}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading asset...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Show error if token is invalid
  if (error) {
    return (
      <Container size="sm" py="xl">
        <Stack gap="lg">
          <div>
            <Title order={2} mb="xs">
              Asset Not Found
            </Title>
            <Text size="sm" c="dimmed">
              The QR code you scanned could not be found
            </Text>
          </div>

          <Alert icon={<IconAlertCircle size={16} />} title="Invalid Token" color="red">
            This QR code token is invalid or has expired. Please check the QR code and try
            again, or contact your administrator.
          </Alert>

          <Text size="sm" c="dimmed">
            Token: <code>{token}</code>
          </Text>
        </Stack>
      </Container>
    );
  }

  // Fallback loading state
  return (
    <Container size="sm" py="xl">
      <Center style={{ minHeight: '50vh' }}>
        <Loader size="lg" />
      </Center>
    </Container>
  );
}
